import { Activity, RefreshCw } from 'lucide-react'
import { motion } from 'framer-motion'
import type { SystemHealth } from '../services/adminApi'

interface SystemStatusCardProps {
  systemHealth: SystemHealth | null
  onRefresh?: () => void
  isRefreshing?: boolean
  showRefreshButton?: boolean
  className?: string
}

export default function SystemStatusCard({
  systemHealth,
  onRefresh,
  isRefreshing = false,
  showRefreshButton = true,
  className = '',
}: SystemStatusCardProps) {
  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / (24 * 60 * 60))
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((seconds % (60 * 60)) / 60)

    const parts = []
    if (days > 0) {
      parts.push(`${days}d`)
    }
    if (hours > 0) {
      parts.push(`${hours}h`)
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`)
    }

    return parts.length > 0 ? parts.join(' ') : '< 1m'
  }

  if (!systemHealth) {
    return (
      <div
        className={`rounded-md border border-gray-200 bg-gray-100 p-4 dark:border-gray-600 dark:bg-gray-700 ${className}`}
      >
        <div className="flex items-center">
          <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-gray-400"></div>
          <span className="text-gray-600 dark:text-gray-400">
            Loading system status...
          </span>
        </div>
      </div>
    )
  }

  const isHealthy =
    systemHealth.system.status === 'operational' &&
    systemHealth.database.status === 'healthy'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`rounded-md border p-4 ${
        isHealthy
          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
          : 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
      } ${className}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Activity
            className={`mr-2 h-5 w-5 ${
              isHealthy ? 'text-green-500' : 'text-yellow-500'
            }`}
          />
          <span
            className={`font-medium ${
              isHealthy
                ? 'text-green-800 dark:text-green-200'
                : 'text-yellow-800 dark:text-yellow-200'
            }`}
          >
            System Status: {isHealthy ? 'Operational' : 'Warning'}
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-4 text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              Uptime: {formatUptime(systemHealth.system.uptime)}
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              DB: {systemHealth.database.responseTime}ms
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Memory: {systemHealth.memory.used}MB/{systemHealth.memory.total}MB
            </span>
          </div>
          {showRefreshButton && onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
